<!--
/**
 * Admin → Configuration → Bulk Import host page.
 *
 * Renders one card per registered importer (grouped by category) that
 * lets HR download the CSV template and upload a populated CSV. All
 * per-card logic (file pick, upload, result display) lives in the
 * sibling BulkImportCard component.
 */
-->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text class="orangehrm-main-title">
        {{ $t('admin.bulk_import') || 'Bulk Import' }}
      </oxd-text>
      <oxd-divider />
      <div class="ensibuuko-bulk-import-intro">
        <oxd-text class="orangehrm-sub-title">
          {{ $t('general.note') }}:
        </oxd-text>
        <ul>
          <li>
            <oxd-text class="orangehrm-information-card-text">
              Download a template, populate it with your data, and upload it
              here. Header column order must not change.
            </oxd-text>
          </li>
          <li>
            <oxd-text class="orangehrm-information-card-text">
              Each upload runs in a single database transaction. Per-row errors
              are reported in the result; a failed row never blocks the rest of
              the file.
            </oxd-text>
          </li>
          <li>
            <oxd-text class="orangehrm-information-card-text">
              {{ $t('general.accepts_up_to_n_mb', {count: formattedFileSize}) }}
            </oxd-text>
          </li>
        </ul>
      </div>

      <div
        v-for="(items, group) in groupedImporters"
        :key="group"
        class="ensibuuko-bulk-import-group"
      >
        <oxd-text tag="h6" class="orangehrm-sub-title">
          {{ groupLabel(group) }}
        </oxd-text>
        <div class="ensibuuko-bulk-import-cards">
          <bulk-import-card
            v-for="item in items"
            :key="item.type"
            :importer="item"
            :allowed-file-types="allowedFileTypes"
            :max-file-size="maxFileSize"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BulkImportCard from '@/orangehrmAdminPlugin/components/BulkImportCard.vue';

const GROUP_LABELS = {
  users: 'Users & Access',
  organization: 'Organization Structure',
  leave: 'Leave Configuration',
  qualifications: 'Qualifications',
  people: 'Employees',
};

export default {
  name: 'BulkImport',
  components: {
    'bulk-import-card': BulkImportCard,
  },
  props: {
    importers: {
      type: Array,
      required: true,
    },
    allowedFileTypes: {
      type: Array,
      required: true,
    },
    maxFileSize: {
      type: Number,
      required: true,
    },
  },
  computed: {
    groupedImporters() {
      const groups = {};
      for (const importer of this.importers) {
        const key = importer.group || 'organization';
        if (!groups[key]) groups[key] = [];
        groups[key].push(importer);
      }
      // Sort items inside each group alphabetically by label
      Object.values(groups).forEach((items) =>
        items.sort((a, b) => a.label.localeCompare(b.label)),
      );
      return groups;
    },
    formattedFileSize() {
      return Math.round((this.maxFileSize / (1024 * 1024)) * 100) / 100;
    },
  },
  methods: {
    groupLabel(group) {
      return GROUP_LABELS[group] || group;
    },
  },
};
</script>

<style lang="scss" scoped>
.ensibuuko-bulk-import-intro {
  margin: 0.5rem 0 1.25rem 0;
}
.ensibuuko-bulk-import-group {
  margin-top: 1.5rem;
}
.ensibuuko-bulk-import-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
  margin-top: 0.5rem;
}
</style>
